import { useEffect, useMemo, useRef, useState } from "react";
import { ICustomerUser } from "../models/ICustomerUser";
import { IRole } from "../models/IUserRoles";
import { getRoleList, getUserListByRoleIds, getUserRoles } from "../services/auth";
import {
  fetchRolesInBatches, getInitials, RoleBadges, RolesState, UserDetailModal,
} from "./RestaurantUsersTab";

function toRoleArray(data: unknown): IRole[] {
  if (Array.isArray(data)) return data as IRole[];
  const wrapped = (data as { roleList?: IRole[] } | null)?.roleList;
  return Array.isArray(wrapped) ? wrapped : [];
}

/**
 * Seçilen rollerden HERHANGİ BİRİNE sahip kullanıcılar (uç birden çok rolü OR'lar).
 * Liste yanıtı rolleri taşımadığı için her kullanıcının rolleri ayrıca, küçük gruplarla çekilir.
 */
export default function AllUsersTab() {
  const [roles, setRoles] = useState<IRole[] | null>(null);
  const [rolesError, setRolesError] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [users, setUsers] = useState<ICustomerUser[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [rolesByUser, setRolesByUser] = useState<Record<string, RolesState>>({});
  const [detailUser, setDetailUser] = useState<ICustomerUser | null>(null);
  const requestId = useRef(0);

  useEffect(() => {
    getRoleList()
      .then((res) => setRoles(
        toRoleArray(res.data).sort((a, b) => (a.roleName ?? "").localeCompare(b.roleName ?? "", "tr"))
      ))
      .catch(() => setRolesError("Rol listesi alınamadı."));
  }, []);

  const loadUsers = (roleIds: string[]) => {
    const mine = ++requestId.current;
    if (roleIds.length === 0) {
      setUsers(null);
      setRolesByUser({});
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    getUserListByRoleIds(roleIds)
      .then((res) => {
        if (mine !== requestId.current) return;
        const list = (res.data ?? []).slice().sort((a, b) =>
          `${a.customerName} ${a.customerSurName}`.localeCompare(`${b.customerName} ${b.customerSurName}`, "tr"));
        setUsers(list);
        setRolesByUser(Object.fromEntries(list.map((u) => [u.id, "loading" as RolesState])));
        fetchRolesInBatches(list.map((u) => u.id), 6, (id, r) => {
          if (mine === requestId.current) setRolesByUser((prev) => ({ ...prev, [id]: r }));
        });
      })
      .catch(() => {
        if (mine !== requestId.current) return;
        setUsers(null);
        setError("Kullanıcılar alınamadı.");
      })
      .finally(() => { if (mine === requestId.current) setLoading(false); });
  };

  const toggleRole = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
    loadUsers(Array.from(next));
  };

  const selectAll = () => {
    const all = new Set((roles ?? []).map((r) => r.id));
    setSelected(all);
    loadUsers(Array.from(all));
  };

  const clearAll = () => {
    setSelected(new Set());
    loadUsers([]);
  };

  const refreshUserRoles = async (userId: string) => {
    try {
      const res = await getUserRoles(userId);
      setRolesByUser((prev) => ({ ...prev, [userId]: res.data ?? [] }));
    } catch {
      setRolesByUser((prev) => ({ ...prev, [userId]: "error" }));
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    if (!users) return [];
    if (!q) return users;
    return users.filter((u) =>
      [u.customerName, u.customerSurName, u.customerEmail, u.customerTelNo, u.customerUserName,
        u.restourantUser?.restourants?.restaurantName]
        .some((s) => (s ?? "").toLocaleLowerCase("tr").includes(q))
    );
  }, [users, search]);

  return (
    <div className="allusers">
      {/* Rol seçimi */}
      <div className="allusers__roles">
        <div className="allusers__roles-head">
          <span><i className="bi bi-funnel-fill me-2"></i>Role göre filtrele</span>
          {roles && roles.length > 0 && (
            <div className="allusers__roles-actions">
              <button type="button" onClick={selectAll} disabled={selected.size === roles.length}>Tümünü seç</button>
              <button type="button" onClick={clearAll} disabled={selected.size === 0}>Temizle</button>
            </div>
          )}
        </div>

        {rolesError ? (
          <div className="roles__alert roles__alert--error"><i className="bi bi-exclamation-circle-fill"></i><span>{rolesError}</span></div>
        ) : !roles ? (
          <div className="allusers__muted"><span className="spinner-border spinner-border-sm me-2" />Roller yükleniyor…</div>
        ) : (
          <div className="allusers__chips">
            {roles.map((r) => {
              const on = selected.has(r.id);
              return (
                <button key={r.id} type="button"
                  className={`allusers__chip${on ? " allusers__chip--on" : ""}${r.roleIsEnabled ? "" : " allusers__chip--off"}`}
                  onClick={() => toggleRole(r.id)} aria-pressed={on}
                  title={r.roleIsEnabled ? r.roleDescription || r.roleName : "Pasif rol"}>
                  {on && <i className="bi bi-check-lg"></i>}
                  {r.roleName}
                </button>
              );
            })}
          </div>
        )}
        {selected.size > 1 && (
          <div className="allusers__hint">Seçili rollerden herhangi birine sahip kullanıcılar listelenir.</div>
        )}
      </div>

      {/* Sonuç */}
      {selected.size === 0 ? (
        <div className="roles__center">
          <i className="bi bi-people"></i>
          <div>Kullanıcıları görmek için yukarıdan bir ya da daha fazla rol seçin.</div>
        </div>
      ) : (
        <>
          <div className="allusers__toolbar">
            <div className="roles__search">
              <i className="bi bi-search"></i>
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Ad, e-posta, telefon ya da restoran ara..." />
              {search && <button type="button" onClick={() => setSearch("")}><i className="bi bi-x-lg"></i></button>}
            </div>
            {users && (
              <span className="allusers__count">
                {search ? `${filtered.length} / ${users.length}` : users.length} kullanıcı
              </span>
            )}
          </div>

          {error && (
            <div className="roles__alert roles__alert--error"><i className="bi bi-exclamation-circle-fill"></i><span>{error}</span></div>
          )}

          {loading && !users ? (
            <div className="roles__center"><span className="spinner-border" /></div>
          ) : users && filtered.length === 0 ? (
            <div className="roles__center">
              <i className="bi bi-person-x"></i>
              <div>{search ? `"${search}" ile eşleşen kullanıcı yok.` : "Bu rollerde kullanıcı yok."}</div>
            </div>
          ) : (
            <div className={`allusers__grid${loading ? " allusers__grid--loading" : ""}`}>
              {filtered.map((u) => {
                const restaurant = u.restourantUser?.restourants?.restaurantName;
                return (
                  <button key={u.id} type="button" className="allusers__card" onClick={() => setDetailUser(u)}>
                    <div className="allusers__card-top">
                      <div className="allusers__avatar">{getInitials(u.customerName, u.customerSurName) || "?"}</div>
                      <div className="allusers__who">
                        <div className="allusers__name">{`${u.customerName ?? ""} ${u.customerSurName ?? ""}`.trim() || u.customerUserName || "—"}</div>
                        <div className="allusers__mail">{u.customerEmail || "—"}</div>
                      </div>
                      <span className={`role-card__status${u.customerIsEnabled ? " role-card__status--on" : ""}`}>
                        {u.customerIsEnabled ? "Aktif" : "Pasif"}
                      </span>
                    </div>
                    <div className="allusers__meta">
                      {u.customerTelNo && <span><i className="bi bi-telephone me-1"></i>{u.customerTelNo}</span>}
                      {restaurant && <span><i className="bi bi-shop me-1"></i>{restaurant}</span>}
                    </div>
                    <RoleBadges roles={rolesByUser[u.id]} />
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {detailUser && (
        <UserDetailModal
          user={detailUser}
          roles={rolesByUser[detailUser.id]}
          onRolesChanged={() => refreshUserRoles(detailUser.id)}
          onClose={() => setDetailUser(null)}
        />
      )}
    </div>
  );
}
